import './actionform.scss'

import {RegisterFormType} from 'campagne/types'
import {FormikValues, useFormikContext} from 'formik'
import React, {FunctionComponent, useEffect} from 'react'
import {AlertGreyIcon, LocationIcon} from 'styles/inline-icons'
import {style} from 'util/campagne/style'
import {useTranslation} from 'util/i18n'
import {RegisterActionSection, RegisterActionTitle} from 'views/campagne/pages'
import {Theme} from 'views/campagne/theme'
import {DatePicker, DatePickerMultiple} from '../datepicker'
import {
	fieldStyles,
	FormField,
	FormfieldCheckbox,
	FormfieldFile,
	FormfieldSelect
} from '../formfield'
import {PickSchool} from './pickschool'

export const styles = style('actionform')

export const ActionForm: FunctionComponent<RegisterFormType> = ({
	school,
	type,
	private_label,
	subscription_required_label,
	location,
	organisator,
	organisation,
	external_url_label,
	external_url_placeholder,
	external_url_note,
	keywords,
	image,
	contactperson,
	description
}) => {
	const {setFieldValue, values, setValues} = useFormikContext<FormikValues>()
	const t = useTranslation('campagne').register
	const hasEndhour = values['date_has_end_hour']
	const hasEndDate = values['date_has_multiple_days']
	const startDate = values['date_start']
	const showContactpersonFields =
		values['contactperson_different_from_organisator']
	useEffect(() => {
		if (!hasEndhour) setFieldValue('hour_end', null)
		if (!hasEndDate) setFieldValue('date_end', startDate)
	}, [hasEndhour, hasEndDate])
	return (
		<div class={styles}>
			<div class={styles('container')}>
				<RegisterActionSection>
					<RegisterActionTitle>{type?.label}</RegisterActionTitle>
					<FormfieldSelect
						name="type"
						keyToData={(key) => setFieldValue('type', key)}
						groups={[
							{key: 'school', value: type.school_label},
							{key: 'group', value: type.group_label},
							{key: 'company', value: type.company_label},
							{key: 'other', value: type.other_label}
						]}
					/>
				</RegisterActionSection>
				{values.type === 'school' || values.type[0] === 'school' ? (
					<RegisterActionSection>
						<RegisterActionTitle>{school?.title}</RegisterActionTitle>
						<PickSchool
							school_location_placeholder={school?.location_placeholder}
							school_location_label={school?.location_label}
							school_name_label={school?.name_label}
						/>
					</RegisterActionSection>
				) : (
					<RegisterActionSection>
						<RegisterActionTitle>{t.organisation}</RegisterActionTitle>
						<FormField
							labelFlex="row"
							name={'organisation_name'}
							label={organisation?.name_label}
							placeholder={organisation?.name_placeholder}
							small
						/>
					</RegisterActionSection>
				)}
				<RegisterActionSection>
					<DatePickerMultiple
						date_start_name={'date_start'}
						date_end_name={'date_end'}
						hour_start_name={'hour_start'}
						hour_end_name={'hour_end'}
						endHourName={'date_has_end_hour'}
						multipleDaysName={'date_has_multiple_days'}
						values={values}
						small
					/>
				</RegisterActionSection>
				<RegisterActionSection>
					<RegisterActionTitle>{location?.title}</RegisterActionTitle>
					<FormField
						labelFlex="row"
						name="location_name"
						label={location?.name_label}
						placeholder={location?.name_placeholder}
						small
					/>
					<FormField
						labelFlex="row"
						name="location_street"
						label={location?.street_label}
						placeholder={location?.street_placeholder}
						small
					/>
					<div class={styles('row')}>
						<FormField
							labelFlex="row"
							name="location_number"
							label={location?.number_label}
							placeholder={location?.number_placeholder}
							small
						/>
						<FormField
							labelFlex="row"
							name="location_box"
							label={location?.box_label}
							placeholder={location?.box_placeholder}
							small
						/>
					</div>
					<FormField
						labelFlex="row"
						name="location_zip"
						label={location?.zip_label}
						placeholder={location?.zip_placeholder}
						icon={<LocationIcon />}
						small
					/>
					<FormField
						labelFlex="row"
						name="location_city"
						label={location?.city_label}
						placeholder={location?.city_placeholder}
						onChange={(e) => {
							const value = e.target.value
							setValues({...values, location_city: value, affiche_where: value})
						}}
						small
					/>
					<ActionFormItem>
						<FormfieldCheckbox name="location_no_address" small>
							{location?.no_address_label}
						</FormfieldCheckbox>
					</ActionFormItem>
					<ActionFormItem>
						<FormfieldCheckbox name="private" small>
							{private_label}
						</FormfieldCheckbox>
					</ActionFormItem>
				</RegisterActionSection>
				<RegisterActionSection>
					<ActionFormItem>
						<FormfieldCheckbox name="subscription_required" small>
							{subscription_required_label}
						</FormfieldCheckbox>
						<ActionFormShiftedItem>
							<RegisterActionTitle>{organisator?.title}</RegisterActionTitle>
							<FormField
								name="organisator_firstname"
								label={organisator?.firstname_label}
								placeholder={organisator?.firstname_placeholder}
								labelFlex="row"
							/>
							<FormField
								name="organisator_lastname"
								label={organisator?.lastname_label}
								placeholder={organisator?.lastname_placeholder}
								labelFlex="row"
							/>
							<FormField
								name="organisator_street"
								label={organisator?.street_label}
								placeholder={organisator?.street_placeholder}
								labelFlex="row"
							/>
							<div class={styles('row')}>
								<FormField
									name="organisator_number"
									label={organisator?.number_label}
									placeholder={organisator?.number_placeholder}
									labelFlex="row"
								/>
								<FormField
									name="organisator_bus"
									label={organisator?.box_label}
									placeholder={organisator?.box_placeholder}
									labelFlex="row"
								/>
							</div>
							<FormField
								name="organisator_postal_code"
								label={organisator?.zip_label}
								placeholder={organisator?.zip_placeholder}
								labelFlex="row"
							/>
							<FormField
								name="organisator_city"
								label={organisator?.city_label}
								placeholder={organisator?.city_placeholder}
								labelFlex="row"
							/>
							<FormField
								type="phone"
								name="organisator_phone"
								label={organisator?.phone_label}
								placeholder={organisator?.phone_placeholder}
								labelFlex="row"
							/>
							<FormField
								type="email"
								name="organisator_contact_details"
								label={organisator?.contact_details_label}
								placeholder={organisator?.contact_details_placeholder}
								labelFlex="row"
							/>

							{!showContactpersonFields && (
								<ActionFormInfo>
									{organisator?.contact_details_note}
								</ActionFormInfo>
							)}
							<DatePicker
								name="organisator_date_of_birth"
								label={organisator?.birthdate_label}
								placeholder={organisator?.birthdate_placeholder}
								labelFlex="row"
								minDate="1900-01-01"
								maxDate={new Date()}
								yearRange={100}
								small
							/>
							<FormfieldSelect
								name="organisator_gender"
								label={organisator?.gender_label}
								keyToData={(key) => setFieldValue('gender', key)}
								groups={[
									{key: 'male', value: organisator?.gender_male_label},
									{key: 'female', value: organisator?.gender_female_label},
									{key: 'other', value: organisator?.gender_other_label}
								]}
							/>

							{values['subscription_required'] && (
								<ActionFormInfo>{organisator?.privacy_note}</ActionFormInfo>
							)}
						</ActionFormShiftedItem>
					</ActionFormItem>
				</RegisterActionSection>
				<RegisterActionSection>
					<RegisterActionTitle>{contactperson?.title}</RegisterActionTitle>
					<FormfieldCheckbox
						name="contactperson_different_from_organisator"
						small
					>
						{contactperson?.checkbox_label}
					</FormfieldCheckbox>

					{showContactpersonFields && (
						<ActionFormShiftedItem>
							<FormField
								name="contactperson_firstname"
								label={contactperson?.firstname_label}
								placeholder={contactperson?.firstname_placeholder}
								labelFlex="row"
							/>
							<FormField
								name="contactperson_lastname"
								label={contactperson?.lastname_label}
								placeholder={contactperson?.lastname_placeholder}
								labelFlex="row"
							/>
							<FormField
								name="contactperson_street"
								label={contactperson?.street_label}
								placeholder={contactperson?.street_placeholder}
								labelFlex="row"
							/>
							<div className={styles('row')}>
								<FormField
									name="contactperson_number"
									label={contactperson?.number_label}
									placeholder={contactperson?.number_placeholder}
									labelFlex="row"
								/>
								<FormField
									name="contactperson_bus"
									label={contactperson?.box_label}
									placeholder={contactperson?.box_placeholder}
									labelFlex="row"
								/>
							</div>
							<FormField
								name="contactperson_postal_code"
								label={contactperson?.zip_label}
								placeholder={contactperson?.zip_placeholder}
								labelFlex="row"
							/>
							<FormField
								name="contactperson_city"
								label={contactperson?.city_label}
								placeholder={contactperson?.city_placeholder}
								labelFlex="row"
							/>
							<FormField
								type="phone"
								name="contactperson_phone"
								label={contactperson?.phone_details_label}
								placeholder={contactperson?.phone_details_placeholder}
								labelFlex="row"
							/>
							<FormField
								type="email"
								name="contactperson_contact_details"
								label={contactperson?.contact_details_label}
								placeholder={contactperson?.contact_details_placeholder}
								labelFlex="row"
							/>
							<ActionFormInfo>
								{contactperson?.contact_details_note}
							</ActionFormInfo>
							<DatePicker
								name="contactperson_date_of_birth"
								label={contactperson?.birthdate_label}
								placeholder={contactperson?.birthdate_placeholder}
								labelFlex="row"
								minDate="1900-01-01"
								maxDate={new Date()}
								yearRange={100}
								small
							/>
							<FormfieldSelect
								name="contactperson_gender"
								label={contactperson?.gender_label}
								keyToData={(key) => setFieldValue('gender', key)}
								groups={[
									{key: 'male', value: contactperson?.gender_male_label},
									{key: 'female', value: contactperson?.gender_female_label},
									{key: 'other', value: contactperson?.gender_other_label}
								]}
							/>
						</ActionFormShiftedItem>
					)}
				</RegisterActionSection>
				<RegisterActionSection mod="noborder">
					<FormField
						name="description_short"
						label={description?.short_label}
						placeholder={description?.short_placeholder}
						small
					/>
					<ActionNote>{description?.short_note}</ActionNote>
					<FormField
						name="external_url"
						label={external_url_label}
						placeholder={external_url_placeholder}
						small
					/>
					<ActionNote>{external_url_note}</ActionNote>
					<ActionFormItem>
						<label htmlFor={'image'} class={fieldStyles('label').mod('column')}>
							{image?.label}
						</label>
						<FormfieldFile
							name="image"
							label={image?.button}
							placeholder={image?.placeholder}
							buttonText={image?.button}
						/>
						<ActionNote>{image?.note}</ActionNote>
					</ActionFormItem>
				</RegisterActionSection>
			</div>
		</div>
	)
}

const ActionNote: React.FC = ({children}) => (
	<div className={styles('note')}>{children}</div>
)

const ActionFormItem: FunctionComponent = ({children}) => (
	<div class={styles('item')}>{children}</div>
)
const ActionFormInfo: FunctionComponent = ({children}) => (
	<div class={styles('info')}>
		<AlertGreyIcon />
		{children && <Theme.P mod="small">{children}</Theme.P>}
	</div>
)

const ActionFormShiftedItem: FunctionComponent = ({children}) => (
	<div class={styles('shifteditem')}>{children}</div>
)
